/** Export CSV (Excel FR) des stats du tableau de bord provider. */

import { parseObjectCreatedAt } from './dashboardPeriod';
import { isInDeliveryStatusForStats } from './creationsByHour';

export type DashboardExportCategoryRow = {
  name: string;
  total: number;
  claimed: number;
  inDelivery: number;
  restituted: number;
  pctReturned: number;
  pctInDelivery: number;
};

export type DashboardExportHourRow = {
  hour: number;
  found: number;
  restituted: number;
};

export type DashboardExportPayload = {
  periodLabel: string;
  exportedAt: string;
  metrics: {
    total: number;
    foundStatus: number;
    claimed: number;
    toShip: number;
    inTransit: number;
    inDelivery: number;
    restituted: number;
    returnRate: number;
    deliveryRate: number;
  };
  byCategory: DashboardExportCategoryRow[];
  byHour: DashboardExportHourRow[];
};

export type DashboardRawObject = {
  title?: string;
  objectType?: string;
  object_type?: string;
  status?: string;
  createdAt?: string;
  created_at?: string;
  claimedAt?: string | null;
  claimed_at?: string | null;
  updatedAt?: string | null;
  updated_at?: string | null;
};

export type DashboardRawObjectRow = {
  registrationDay: string;
  registrationHour: string;
  category: string;
  title: string;
  restitutionType: string;
  restitutionDay: string;
  restitutionHour: string;
};

export type DashboardRawExportPayload = {
  objects: DashboardRawObjectRow[];
};

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDay(date: Date): string {
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
}

function formatHour(date: Date): string {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function objectCategory(o: DashboardRawObject): string {
  return String(o.objectType || o.object_type || 'Autre').trim() || 'Autre';
}

function objectTitle(o: DashboardRawObject): string {
  return String(o.title || '').trim() || '-';
}

function resolveRestitution(o: DashboardRawObject): {
  type: string;
  at: Date | null;
} {
  if (o.status === 'claimed') {
    const at =
      parseObjectCreatedAt(o.claimedAt ?? o.claimed_at) ??
      parseObjectCreatedAt(o.updatedAt ?? o.updated_at) ??
      parseObjectCreatedAt(o.createdAt ?? o.created_at);
    return { type: 'Main propre', at };
  }

  if (isInDeliveryStatusForStats(o.status)) {
    const at =
      parseObjectCreatedAt(o.updatedAt ?? o.updated_at) ??
      parseObjectCreatedAt(o.createdAt ?? o.created_at);
    return { type: 'Livraison', at };
  }

  return { type: '', at: null };
}

export function buildRawObjectRows(objects: DashboardRawObject[]): DashboardRawObjectRow[] {
  const rows: DashboardRawObjectRow[] = [];
  for (const o of objects) {
    const created = parseObjectCreatedAt(o.createdAt ?? o.created_at);
    if (!created) continue;

    const restitution = resolveRestitution(o);
    rows.push({
      registrationDay: formatDay(created),
      registrationHour: formatHour(created),
      category: objectCategory(o),
      title: objectTitle(o),
      restitutionType: restitution.type,
      restitutionDay: restitution.at ? formatDay(restitution.at) : '',
      restitutionHour: restitution.at ? formatHour(restitution.at) : '',
    });
  }
  return rows;
}

function csvEscape(value: string | number): string {
  const raw = String(value ?? '');
  if (/[;"\n\r]/.test(raw)) {
    return `"${raw.replace(/"/g, '""')}"`;
  }
  return raw;
}

function csvRow(cells: Array<string | number>): string {
  return cells.map(csvEscape).join(';');
}

function downloadCsv(csvBody: string, filename: string): void {
  const blob = new Blob([`\uFEFF${csvBody}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function buildDashboardStatsCsv(payload: DashboardExportPayload): string {
  const lines: string[] = [];
  const m = payload.metrics;

  lines.push(csvRow(['Tableau de bord Sherlook - export stats']));
  lines.push(csvRow(['Période', payload.periodLabel]));
  lines.push(csvRow(['Exporté le', payload.exportedAt]));
  lines.push('');

  lines.push(csvRow(['Synthèse']));
  lines.push(csvRow(['Indicateur', 'Valeur']));
  lines.push(csvRow(['Total des annonces', m.total]));
  lines.push(csvRow(['Objets trouvés (total)', m.total]));
  lines.push(csvRow(['Sur place (statut trouvé)', m.foundStatus]));
  lines.push(csvRow(['Restitués (main propre)', m.claimed]));
  lines.push(csvRow(['À expédier', m.toShip]));
  lines.push(csvRow(['En transit', m.inTransit]));
  lines.push(csvRow(['Partis en livraison', m.inDelivery]));
  lines.push(csvRow(['Objets restitués (claimed + livraison)', m.restituted]));
  lines.push(csvRow(['Taux de restitution (%)', m.returnRate]));
  lines.push(csvRow(['Taux partis en livraison (%)', m.deliveryRate]));
  lines.push('');

  lines.push(csvRow(['Répartition par catégorie']));
  lines.push(
    csvRow([
      'Catégorie',
      'Objets trouvés',
      'Restitués main propre',
      'Partis en livraison',
      'Restitués (total)',
      'Taux restitution (%)',
      'Taux livraison (%)',
    ]),
  );
  for (const row of payload.byCategory) {
    lines.push(
      csvRow([
        row.name,
        row.total,
        row.claimed,
        row.inDelivery,
        row.restituted,
        row.pctReturned,
        row.pctInDelivery,
      ]),
    );
  }
  lines.push('');

  lines.push(csvRow(['Activité par heure']));
  lines.push(csvRow(['Heure', 'Déclarations', 'Restitutions']));
  for (const row of payload.byHour) {
    lines.push(
      csvRow([
        `${String(row.hour).padStart(2, '0')}h`,
        row.found,
        row.restituted,
      ]),
    );
  }

  return `${lines.join('\r\n')}\r\n`;
}

export function buildDashboardRawDataCsv(payload: DashboardRawExportPayload): string {
  // Première ligne = en-têtes nommés (sans préambule) pour tri/filtre Excel.
  const lines: string[] = [
    csvRow([
      'Jour enregistrement',
      'Heure enregistrement',
      'Catégorie',
      'Titre',
      'Type de restitution',
      'Jour de restitution',
      'Heure de restitution',
    ]),
  ];

  for (const row of payload.objects) {
    lines.push(
      csvRow([
        row.registrationDay,
        row.registrationHour,
        row.category,
        row.title,
        row.restitutionType,
        row.restitutionDay,
        row.restitutionHour,
      ]),
    );
  }

  return `${lines.join('\r\n')}\r\n`;
}

export function downloadDashboardStatsCsv(payload: DashboardExportPayload, filename?: string): void {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  downloadCsv(buildDashboardStatsCsv(payload), filename ?? `sherlook-stats-${stamp}.csv`);
}

export function downloadDashboardRawDataCsv(payload: DashboardRawExportPayload, filename?: string): void {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  downloadCsv(buildDashboardRawDataCsv(payload), filename ?? `sherlook-donnees-brutes-${stamp}.csv`);
}
